import clsx from 'clsx';
import { FC, useCallback, useId, useState } from 'react';

import { SECTIONS_IDS } from '../../../constants/sections';
import { WithClassName } from '../../../types/react';
import { ChevronForwardV4Icon } from '../../icons/ChevronForwardV4Icon';
import { ButtonLink } from '../common/ButtonLink';
import { Container } from '../common/Container';
import { Flex } from '../common/Flex';
import { Text } from '../common/Text';

import styles from './FAQBlock.module.scss';

type FAQBlockProps = WithClassName & {};

const FAQ_ITEMS = [
  {
    question: 'What payment methods does SmartGates support?',
    answer:
      'We support a wide range of payment instruments to ensure global coverage. This includes major credit and debit cards (Visa, Mastercard, Maestro, MIR), popular e-wallets, Apple Pay, and Google Pay. Additionally, we provide access to local payment methods depending on your business region, such as bank transfers and alternative local systems.',
  },
  {
    question: 'How does the smart routing engine work?',
    answer:
      'Our engine analyzes every transaction request in real-time. Based on parameters like card type, issuing bank, geolocation, and current acquirer performance, the algorithm automatically selects the optimal path for the payment. This technology maximizes your conversion rates (approval rates) and minimizes processing costs.',
  },
  {
    question: 'What security standards does SmartGates comply with?',
    answer:
      'Security is our core priority. SmartGates is fully certified with PCI DSS Level 1, the highest security standard in the payment card industry. We also utilize 3D-Secure 2.0 protocols, advanced AI-driven anti-fraud systems, and end-to-end data encryption to protect every transaction.',
  },
  {
    question: 'How long does integration take?',
    answer:
      'Thanks to our comprehensive API documentation and ready-to-use modules for popular CMS platforms, the technical integration can be completed within a few hours to a couple of days. The full account activation also depends on the KYC (Know Your Customer) verification process, which typically takes 1–3 business days.',
  },
  {
    question: 'What reporting and analytics are available?',
    answer:
      'You get access to a powerful dashboard with real-time analytics in your merchant portal. You can track turnover dynamics, average transaction value, payer geography, and detailed breakdown of decline reasons. All data can be easily exported in CSV or XLS formats for your internal accounting and reporting.',
  },
  {
    question: 'Is there a minimum transaction volume required?',
    answer:
      'We work with businesses of all sizes, from startups to large enterprises. There is no strict minimum transaction volume required. However, we offer tiered pricing models: as your transaction volume grows, you can benefit from lower processing fees per operation.',
  },
] as const;

export const FAQBlock: FC<FAQBlockProps> = (props) => {
  const { className } = props;

  const idPrefix = useId().replace(/:/g, '');
  const [openIds, setOpenIds] = useState<Record<number, boolean>>({});

  const toggleItem = useCallback((index: number) => {
    setOpenIds((prev) => ({ ...prev, [index]: !prev[index] }));
  }, []);

  return (
    <Container className={clsx(styles.root, className)} id={SECTIONS_IDS.faq}>
      <div className={styles.inner}>
        <Flex direction="column" alignItems="center" className={styles.intro}>
          <Text className={styles.title} align="center">
            Frequently Asked Questions
          </Text>

          <Text type="regular16" color="white50" align="center">
            Everything you need to know about SmartGates payment platform
          </Text>
        </Flex>

        <div className={styles.list} role="list">
          {FAQ_ITEMS.map(({ question, answer }, index) => {
            const isOpen = Boolean(openIds[index]);
            const regionId = `faq-panel-${idPrefix}-${index}`;
            const labelId = `faq-label-${idPrefix}-${index}`;

            return (
              <article key={question} className={styles.item} role="listitem">
                <button
                  type="button"
                  id={labelId}
                  aria-expanded={isOpen}
                  aria-controls={regionId}
                  className={styles.itemButton}
                  onClick={() => toggleItem(index)}
                >
                  <span className={styles.itemQuestionText}>{question}</span>

                  <span aria-hidden className={clsx(styles.chevronWrap, isOpen && styles.open)}>
                    <ChevronForwardV4Icon />
                  </span>
                </button>

                <div className={clsx(styles.answerAccordion, isOpen && styles.answerAccordion_open)}>
                  <div className={styles.answerAccordionInner}>
                    <div
                      id={regionId}
                      className={styles.answer}
                      role="region"
                      aria-labelledby={labelId}
                      aria-hidden={!isOpen}
                    >
                      <Text className={styles.answerText} type="regular16" color="white50" whiteSpace="normal">
                        {answer}
                      </Text>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <Flex direction="column" alignItems="center" className={styles.ctaWrap}>
          <Text type="regular16" color="white50" align="center">
            Still have questions?
          </Text>

          <ButtonLink href={`#${SECTIONS_IDS.contact}`}>Contact our team</ButtonLink>
        </Flex>
      </div>
    </Container>
  );
};
